import type { SupportedHeaderPhotoLayoutSchema } from '@shared/types/WebsiteHeaderSettings.types';
import { addRendition } from '@shared/utils/photoRendition';

/**
 * Number of photos that are immediately visible (critical) per header layout.
 * Animated layouts (photoSlider, photoMarquee) only show the first 1-2 photos initially.
 */
const CRITICAL_PHOTO_COUNT: Record<SupportedHeaderPhotoLayoutSchema['presentationLayout'], number> = {
  center: 1,
  fullWidth: 1,
  pageWidth: 1,
  photoArch: 1,
  photoBackground: 1,
  photoDuoLeft: 2,
  photoDuoRight: 2,
  photoTrio: 3,
  photoTrioPageWidth: 3,
  photoSlider: 2,
  photoMarquee: 3,
  monogram: 0,
  noPhoto: 0,
  videoBackground: 1,
  videoArch: 1,
};

type HeaderPhotoUrlsResult = Readonly<{
  allUrls: ReadonlyArray<string>;
  criticalCount: number;
}>;

/**
 * Extracts photo URLs and critical count from a websiteHeader's headerPhoto schema.
 *
 * For photo-based layouts, reads from `headerPhoto.photos[].url`.
 * For video layouts, reads from `headerPhoto.video.placeholderImageUrl` (the thumbnail).
 */
export function getHeaderPhotoUrls(headerPhoto: SupportedHeaderPhotoLayoutSchema | null | undefined): HeaderPhotoUrlsResult {
  if (!headerPhoto) {
    return { allUrls: [], criticalCount: 0 };
  }

  // Use headerPhoto.presentationLayout directly (not destructured) so TypeScript's
  // discriminated union narrowing applies to `headerPhoto` inside each branch.
  const criticalCount = CRITICAL_PHOTO_COUNT[headerPhoto.presentationLayout] ?? 0;

  // Video layouts use a placeholder image, not the photos array.
  // After this return, TypeScript narrows headerPhoto to non-video layouts.
  if (headerPhoto.presentationLayout === 'videoBackground' || headerPhoto.presentationLayout === 'videoArch') {
    const urls: string[] = [];

    if (headerPhoto.video?.placeholderImageUrl) {
      urls.push(addRendition({ url: headerPhoto.video.placeholderImageUrl, renditionSize: 'xlarge' }));
    } else if (headerPhoto.presentationLayout === 'videoArch' && headerPhoto.photos?.[0]?.url) {
      // videoArch falls back to photos[0] as poster frame when no video placeholderImageUrl
      urls.push(addRendition({ url: headerPhoto.photos[0].url, renditionSize: 'xlarge' }));
    }

    return { allUrls: urls, criticalCount };
  }

  // Photo-based layouts. Each layout defines `photos` with a different element type
  // (e.g. WebsiteHeaderPhotoWithAlignment vs WebsiteHeaderPhotoWithCropInfo), all of which
  // extend WebsitePhotoBaseProperties and expose `url: string`. We guard against layouts
  // without a `photos` key (e.g. future additions) before casting to the minimal common shape.
  const photos = ('photos' in headerPhoto ? (headerPhoto as { photos?: ReadonlyArray<{ url: string }> }).photos : undefined) ?? [];
  const allUrls = photos
    .map(p => p.url)
    .filter((url): url is string => !!url)
    .map(url => addRendition({ url, renditionSize: 'xlarge' }));

  return { allUrls, criticalCount };
}
